// The value model and ROI, ported from the proposal builder prototype
// (olytic-cpq): what the customer gains, risk-weighted, and how the quote pays
// back over time. Pure functions; the model is the quote's `value_model`.

export type Scenario = 'conservative' | 'base' | 'stretch';
export type BenefitType = 'Cost savings' | 'Revenue' | 'Productivity';
export const BENEFIT_TYPES: BenefitType[] = ['Cost savings', 'Revenue', 'Productivity'];
export const UNITS = ['$ / yr', 'hours / yr', 'miles / yr', 'count / yr'] as const;
export const SCENARIOS: Scenario[] = ['conservative', 'base', 'stretch'];

/** Hard savings count in full; revenue and productivity at half, since they are less certain. */
export const RISK_WEIGHT: Record<BenefitType, number> = { 'Cost savings': 1, Revenue: 0.5, Productivity: 0.5 };

export interface Metric {
  id: string;
  name: string;
  /** Today's number, per year. */
  value: number;
  unit: (typeof UNITS)[number];
  /** Dollars per unit; a $ / yr metric is worth its value. */
  rate: number;
  source: string;
  note: string;
}

export interface Assumption {
  id: string;
  metric: string;
  label: string;
  /** Expected change, percent. */
  change: number;
  /** Share of the change captured, percent; blank = all of it. */
  capture: number | null;
  type: BenefitType;
  source: string;
  note: string;
  /** The change under each scenario; the scenario chosen sets `change`. */
  scenarios: Record<Scenario, number>;
  /** Which number the lever's slider moves: the change (default) or the realized share. */
  lever?: 'change' | 'capture';
  /** The slider's top, percent; default 100. */
  max?: number;
}

export interface QualBenefit {
  id: string;
  benefit: string;
  detail: string;
}

export interface ValueModel {
  metrics: Metric[];
  assumptions: Assumption[];
  quals: QualBenefit[];
  scenario: Scenario;
  /** Months from launch to full benefit. */
  ramp: number;
  /** Months from kickoff to launch. */
  goLiveMonths: number;
  /** The license is billed from signing rather than from launch. */
  licenseFromSigning: boolean;
  horizonYears: number;
  /** Customer's EBITDA multiple, for added enterprise value; 0 = not shown. */
  ebitdaMultiple: number;
  ebitdaNote: string;
}

export const EMPTY_VALUE_MODEL: ValueModel = {
  metrics: [],
  assumptions: [],
  quals: [],
  scenario: 'base',
  ramp: 3,
  goLiveMonths: 3,
  licenseFromSigning: false,
  horizonYears: 5,
  ebitdaMultiple: 0,
  ebitdaNote: '',
};

/** A stored value model with every field present (older or partial documents fill in from the defaults). */
export function readValueModel(stored: unknown): ValueModel {
  const model = stored && typeof stored === 'object' ? (stored as Partial<ValueModel>) : {};
  return { ...EMPTY_VALUE_MODEL, ...model };
}

const n = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : Number(value) || 0);

/** One assumption's yearly impact in dollars, before risk weighting. */
export function impactOf(assumption: Assumption, metrics: Metric[]): number {
  const metric = metrics.find((m) => m.id === assumption.metric);
  if (!metric) return 0;
  const rate = metric.unit === '$ / yr' ? 1 : n(metric.rate);
  const capture = assumption.capture === null || assumption.capture === undefined ? 1 : n(assumption.capture) / 100;
  return n(metric.value) * rate * (n(assumption.change) / 100) * capture;
}

export interface ValueTotals {
  /** Yearly benefit at full run rate. */
  gross: number;
  /** Risk-weighted yearly benefit: what pricing to value uses. */
  adjusted: number;
  byDriver: { label: string; value: number; type: BenefitType }[];
}

export function valueTotals(model: ValueModel): ValueTotals {
  let gross = 0;
  let adjusted = 0;
  const byDriver = model.assumptions.map((a) => {
    const value = impactOf(a, model.metrics);
    gross += value;
    adjusted += value * (RISK_WEIGHT[a.type] ?? 1);
    return { label: a.label || model.metrics.find((m) => m.id === a.metric)?.name || 'Benefit', value, type: a.type };
  });
  byDriver.sort((x, y) => y.value - x.value);
  return { gross, adjusted, byDriver };
}

/** The model with every assumption's change set to its value in `scenario`. */
export function withScenario(model: ValueModel, scenario: Scenario): ValueModel {
  return {
    ...model,
    scenario,
    assumptions: model.assumptions.map((a) => ({ ...a, change: a.scenarios?.[scenario] ?? a.change })),
  };
}

export interface Roi {
  /** Cumulative net value by month, from 0 (kickoff, the one-time cost spent) to months. */
  cumulative: number[];
  benefits: number;
  costs: number;
  net: number;
  /** Benefits over costs. */
  multiple: number;
  /** Months from kickoff until cumulative net turns positive; null if not within the horizon. */
  payback: number | null;
  /** Net each year at full run rate: yearly benefit less 12 months of software. */
  runNet: number;
  months: number;
  goLive: number;
  totals: ValueTotals;
}

/**
 * The ROI over `months`: the one-time cost at kickoff, the monthly cost from
 * launch (or from signing), and benefits from launch, ramping to full over
 * `ramp` months.
 */
export function roiModel(model: ValueModel, oneTime: number, monthlyCost: number, months = model.horizonYears * 12): Roi {
  const totals = valueTotals(model);
  const goLive = Math.max(0, n(model.goLiveMonths));
  const ramp = Math.max(0, n(model.ramp));
  const cumulative = [-oneTime];
  let benefits = 0;
  let costs = oneTime;
  let payback: number | null = null;
  for (let m = 1; m <= months; m += 1) {
    const since = m - goLive;
    const benefit = since <= 0 ? 0 : (totals.gross / 12) * (ramp ? Math.min(1, since / ramp) : 1);
    const cost = since > 0 || model.licenseFromSigning ? monthlyCost : 0;
    benefits += benefit;
    costs += cost;
    const prev = cumulative[m - 1] as number;
    const next = prev + benefit - cost;
    cumulative.push(next);
    if (payback === null && next >= 0 && prev < 0) payback = m - 1 + -prev / (next - prev);
  }
  if (payback === null && oneTime <= 0) payback = 0;
  return {
    cumulative,
    benefits,
    costs,
    net: benefits - costs,
    multiple: costs > 0 ? benefits / costs : 0,
    payback,
    runNet: totals.gross - monthlyCost * 12,
    months,
    goLive,
    totals,
  };
}

/** Year-by-year cash for the ROI table: one-time and software costs, benefits, net and running total. */
export function roiByYear(model: ValueModel, oneTime: number, monthlyCost: number, roi: Roi) {
  const rows: { year: number; oneTime: number; software: number; benefit: number; net: number; cumulative: number }[] = [];
  const goLive = roi.goLive;
  const ramp = Math.max(0, n(model.ramp));
  for (let y = 1; y <= Math.ceil(roi.months / 12); y += 1) {
    let software = 0;
    let benefit = 0;
    for (let m = (y - 1) * 12 + 1; m <= Math.min(y * 12, roi.months); m += 1) {
      const since = m - goLive;
      benefit += since <= 0 ? 0 : (roi.totals.gross / 12) * (ramp ? Math.min(1, since / ramp) : 1);
      software += since > 0 || model.licenseFromSigning ? monthlyCost : 0;
    }
    const spent = y === 1 ? oneTime : 0;
    rows.push({ year: y, oneTime: spent, software, benefit, net: benefit - software - spent, cumulative: roi.cumulative[Math.min(y * 12, roi.months)] as number });
  }
  return rows;
}
